import { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  LocalStorageKeys,
  ROUTES,
  RegistrationSteps,
  TranslationContext,
} from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import NameDevice from "@is/features/NameDevice";
import SelectDevice from "@is/features/SelectDevice";
import SelectStore from "@is/features/SelectStore";
import { LocalStorageService } from "@is/services";

const useManageRegistration = () => {
  const { t, i18n } = useTranslation(TranslationContext.DeviceRegistration);
  const navigate = useNavigate();

  const { steps, activeStepIndex, deviceName, updateViewState } =
    useContext(ViewStateContext).registration;

  const activeStep = steps[activeStepIndex];

  const ActiveStepComponent = useMemo(() => {
    if (activeStep.key === RegistrationSteps.SelectStore) {
      LocalStorageService.removeItem(LocalStorageKeys.StoreId);
      i18n.changeLanguage("en");

      return SelectStore;
    }

    if (activeStep.key === RegistrationSteps.SelectDevice) {
      return SelectDevice;
    }

    return NameDevice;
  }, [activeStep.key, i18n]);

  const isNextStepAllowed = useMemo(
    () => !!activeStep.label && !!steps[activeStepIndex + 1],
    [activeStep, activeStepIndex, steps]
  );

  const handleNextStep = useCallback(() => {
    updateViewState({
      activeStepIndex: activeStepIndex + 1,
    });
  }, [activeStepIndex, updateViewState]);

  const handlePreviousStep = useCallback(() => {
    if (activeStepIndex === 0) {
      if (window.history.state?.idx > 0) {
        navigate(-1);
      } else {
        navigate(ROUTES.WelcomeView, { replace: true });
      }
      return;
    }

    if (steps[activeStepIndex].key === RegistrationSteps.NameDevice) {
      const newSteps = [...steps];

      newSteps[activeStepIndex] = {
        ...newSteps[activeStepIndex],
        label: t(deviceName),
      };
      updateViewState({
        steps: newSteps,
        activeStepIndex: activeStepIndex - 1,
      });

      return;
    } else if (steps[activeStepIndex].key === RegistrationSteps.SelectDevice) {
      const newSteps = [...steps];
      delete newSteps[activeStepIndex - 1].label;

      updateViewState({
        steps: newSteps,
        selectedStore: null,
        activeStepIndex: activeStepIndex - 1,
      });

      return;
    }

    updateViewState({
      activeStepIndex: activeStepIndex - 1,
    });
  }, [activeStepIndex, steps, deviceName, updateViewState, navigate, t]);

  const handleStepChange = useCallback(
    (stepIndex: number) => {
      if (stepIndex === activeStepIndex) {
        return;
      }

      if (steps[stepIndex].label || steps[stepIndex - 1]?.label) {
        if (steps[activeStepIndex].key === RegistrationSteps.NameDevice) {
          const newSteps = [...steps];

          newSteps[activeStepIndex] = {
            ...newSteps[activeStepIndex],
            label: t(deviceName),
          };

          updateViewState({
            steps: newSteps,
            activeStepIndex: stepIndex,
          });

          return;
        }
        updateViewState({
          activeStepIndex: stepIndex,
        });
      }
    },
    [activeStepIndex, steps, deviceName, updateViewState, t]
  );

  return {
    steps,
    activeStepIndex,
    activeStep,
    isNextStepAllowed,
    isPreviousStepAllowed: activeStepIndex > 0,
    handleStepChange,
    ActiveStepComponent,
    handleNextStep,
    handlePreviousStep,
  };
};

export default useManageRegistration;
